import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import dayjs from 'dayjs';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import RectangleChip from '../common/RectangleChip.component';
import { StatusLogbook } from '../../module/logbook/conf/Data.conf';
import LinkProductsList from '../item/button/ButtonProductsList.component';
import { DeliveryYachtsStatusLogbookListEdit } from './DeliveryYachtsStatusLogbookListEdit.component';
const getStatusHex = (status) => {
  const normalized = (status || '').toString().trim().toUpperCase();
  return _.find(StatusLogbook, (item) => item?.label === normalized)?.hex;
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD-MM-YYYY') : value;
};

const formatTime = (value) => {
  if (!value) {
    return '-';
  }

  const parsed = dayjs(`2000-01-01T${value}`);
  return parsed.isValid() ? parsed.format('HH:mm') : value;
};

const getManagerPhone = (row) => {
  return (
    _.get(
      row,
      'attributes.delivery_manager_on_board.attributes.phone_number'
    ) ||
    _.get(row, 'attributes.delivery_manager_on_board.attributes.phone') ||
    _.get(
      row,
      'attributes.delivery_manager_on_board.attributes.mobile_phone'
    ) ||
    _.get(
      row,
      'attributes.delivery_manager_on_board.attributes.id_users_permissions_user.data.attributes.phone_number'
    ) ||
    _.get(
      row,
      'attributes.delivery_manager_on_board.attributes.id_users_permissions_user.data.attributes.phone'
    ) ||
    _.get(
      row,
      'attributes.delivery_manager_on_board.attributes.id_users_permissions_user.data.attributes.mobile_phone'
    ) ||
    '-'
  );
};

const normalizePickupSuppliers = (pickupSuppliers) => {
  return Array.isArray(pickupSuppliers) ? pickupSuppliers : [];
};

const PickupSuppliersDetailsDrawer = ({ row, onClose = () => {} }) => {
  const pickupList = useSelector(state => {
    const deliveryYacht = state?.dispatch_board?.data?.data?.find(item => item?.id === row?.id);
    return deliveryYacht?.attributes?.pickup_suppliers_related || [];
  });

  const currentRow = useMemo(() => {
    return {
      ...row,
      attributes: {
        ...row?.attributes,
        pickup_suppliers_related: pickupList,
      },
    };
  }, [pickupList, row]);

  const pickupRows = useMemo(() => {
    const sourceRows = normalizePickupSuppliers(
      _.get(currentRow, 'attributes.pickup_suppliers_related', [])
    );

    return sourceRows.map((item) => {
      const attrs = item?.attributes || item;
      const pickupId = item?.id || attrs?.id || null;

      return {
        id: pickupId,
        supplier: _.get(attrs, 'id_supplier.data.attributes.label') ||
          _.get(attrs, 'id_supplier.label') ||
          '-',
        driverName:
          _.get(attrs, 'id_driver_in_charge.data.attributes.id_users_permissions_user.data.attributes.name') ||
          _.get(attrs, 'id_driver_in_charge.id_users_permissions_user.name') ||
          '-',
        driverSurname:
          _.get(attrs, 'id_driver_in_charge.data.attributes.id_users_permissions_user.data.attributes.surname') ||
          _.get(attrs, 'id_driver_in_charge.id_users_permissions_user.surname') ||
          '',
        driverPhone:
          _.get(attrs, 'id_driver_in_charge.data.attributes.phone_number') ||
          _.get(attrs, 'id_driver_in_charge.data.attributes.phone') ||
          _.get(attrs, 'id_driver_in_charge.data.attributes.mobile_phone') ||
          _.get(attrs, 'id_driver_in_charge.data.attributes.id_users_permissions_user.data.attributes.phone_number') ||
          _.get(attrs, 'id_driver_in_charge.data.attributes.id_users_permissions_user.data.attributes.phone') ||
          _.get(attrs, 'id_driver_in_charge.data.attributes.id_users_permissions_user.data.attributes.mobile_phone') ||
          _.get(attrs, 'id_driver_in_charge.phone_number') ||
          _.get(attrs, 'id_driver_in_charge.phone') ||
          _.get(attrs, 'id_driver_in_charge.mobile_phone') ||
          _.get(attrs, 'id_driver_in_charge.id_users_permissions_user.phone_number') ||
          _.get(attrs, 'id_driver_in_charge.id_users_permissions_user.phone') ||
          _.get(attrs, 'id_driver_in_charge.id_users_permissions_user.mobile_phone') ||
          '-',
        statusLogbook: attrs?.status_logbook || '-',
        time: attrs?.collection_delivery_time || '-',
        date: attrs?.collection_delivery_date || '-',
        location: attrs?.id_location?.marina || '-',
        department: attrs?.id_department?.name_department || '-',
        id_list: !_.isEmpty(attrs?.id_list_items)
          ? {
            ...attrs?.id_list_items[0]?.id_list,
            check_depot_department: {
              id_department:
                attrs?.id_department?.short_description,
              check_depot:
                attrs?.id_list_items[0]?.id_list?.check_depot,
            },
          }
          : null,
        yacht_name: attrs?.id_yacht?.yacht_name || '-',
        service: attrs?.id_list_items[0]?.id_list?.id_operation_list?.id_service?.service || '-',
        status_logbook: attrs?.status_logbook || '-',
      };
    });
  }, [currentRow]);

  const deliveryStatus = _.get(currentRow, 'attributes.status_logbook_id.status_logbook') || '-';
  const deliveryInfo = useMemo(
    () => ({
      yachtName:
        _.get(currentRow, 'attributes.id_yacht.data.attributes.yacht_name') || '-',
      id: currentRow?.id ? `#${currentRow.id}` : '-',
      deliveryDate: formatDate(_.get(currentRow, 'attributes.collection_delivery_date')),
      deliveryTime: formatTime(_.get(currentRow, 'attributes.collection_delivery_time')),
      driver:
        `${_.get(
          currentRow,
          'attributes.id_driver_in_charge_id.id_driver_in_charge.name',
          ''
        )
          } ${_.get(
            currentRow,
            'attributes.id_driver_in_charge_id.id_driver_in_charge.surname',
            ''
          )
          }`.trim() || '-',
      location:
        _.get(currentRow, 'attributes.id_location.data.attributes.marina') || '-',
      managerName: `${_.get(
        currentRow,
        'attributes.delivery_manager_on_board.attributes.id_users_permissions_user.data.attributes.name',
        ''
      )
        } ${_.get(
          currentRow,
          'attributes.delivery_manager_on_board.attributes.id_users_permissions_user.data.attributes.surname',
          ''
        )
        }`.trim() || '-',
      managerPhone: getManagerPhone(currentRow),
      pickupDepartments: currentRow.attributes?.pickup_departments || [],
    }),
    [currentRow]
  );

  return (
    <Stack spacing={2.5}>
      <Typography variant='h4' sx={{ fontWeight: 700 }}>
        PICKUP DETAILS
      </Typography>

      <Paper variant='outlined' sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            p: 1.5,
            bgcolor: '#F8FAFC',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Stack direction='row' alignItems='center' spacing={1}>
              <LocalShippingIcon sx={{ color: '#2563EB', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: '#2563EB' }}>
                DELIVERY YACHT
              </Typography>
            </Stack>
            <RectangleChip
              width='auto'
              value={(deliveryStatus || '-').toString().toUpperCase()}
              color='#fff'
              backgroundColor={getStatusHex(deliveryStatus) || '#005488'}
              sx={{ boxShadow: 0 }}
            />
          </Stack>
        </Box>

        <Box sx={{ p: 2 }}>
          <Stack spacing={1.25}>
            <Stack direction='row' justifyContent='space-between'>
              <Typography color='text.secondary'>YACHT NAME</Typography>
              <Typography sx={{ fontWeight: 700 }}>{deliveryInfo.yachtName}</Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography color='text.secondary'>ID</Typography>
              <Typography sx={{ fontWeight: 700, color: '#2563EB' }}>{deliveryInfo.id}</Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography color='text.secondary'>DATE</Typography>
              <Typography sx={{ fontWeight: 700 }}>{deliveryInfo.deliveryDate}</Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography color='text.secondary'>TIME</Typography>
              <Typography sx={{ fontWeight: 700 }}>{deliveryInfo.deliveryTime}</Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography color='text.secondary'>DRIVER</Typography>
              <Typography sx={{ fontWeight: 700 }}>{deliveryInfo.driver}</Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography color='text.secondary'>LOCATION</Typography>
              <Typography sx={{ fontWeight: 700 }}>{deliveryInfo.location}</Typography>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography color='text.secondary'>MANAGER ON BOARD</Typography>
              <Stack alignItems='flex-end'>
                <Typography sx={{ fontWeight: 700 }}>{deliveryInfo.managerName}</Typography>
                {deliveryInfo.managerPhone && deliveryInfo.managerPhone !== '-' && (
                  <Typography
                    variant='body1'
                    fontWeight={'600'}
                    alignItems={'end'}
                    display={'flex'}
                    justifyItems={'center'}
                  >
                    <LocalPhoneIcon style={{ width: 16, paddingRight: 2 }} />
                    {deliveryInfo.managerPhone}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Stack direction='row' justifyContent='space-between'>
              <Typography color='text.secondary'>DEPARTMENTS INVOLVED</Typography>
              <Typography sx={{ fontWeight: 700 }}>{deliveryInfo.pickupDepartments?.join(', ')}</Typography>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      <Paper variant='outlined' sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2 }}>
          <Typography variant='h6' sx={{ fontWeight: 700 }}>
            Linked pickups ({pickupRows.length})
          </Typography>
        </Box>
        <Divider />

        {pickupRows.length === 0 ? (
          <Box p={2}>
            <Typography color='text.secondary'>No linked pickup suppliers.</Typography>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 2fr 1.5fr 1fr 0.5fr 2fr 1.5fr 1fr',
                px: 2,
                py: 1.25,
                bgcolor: '#F8FAFC',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant='caption' sx={{ fontWeight: 700 }}>
                DEPARTMENT
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 700 }}>
                SUPPLIER
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 700 }}>
                PRODUCTS
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 700 }}>
                DATE
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 700 }}>
                TIME
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 700 }}>
                LOCATION
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 700 }}>
                DRIVER
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 700 }}>
                STATUS
              </Typography>
            </Box>

            {pickupRows.map((item) => {
              const driverFullName = `${item?.driverName || ''} ${item?.driverSurname || ''}`.trim() || '-';

              return (
                <Box
                  key={item?.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 2fr 1.5fr 1fr 0.5fr 2fr 1.5fr 1fr',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>{item?.department}</Typography>
                  <Stack>
                    <Typography sx={{ fontWeight: 700 }}>{item?.supplier || '-'}</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      PICKUP ID: {item?.id ? `#${item.id}` : '-'}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontWeight: 600 }}>
                    {!_.isEmpty(item?.id_list) ? (
                      <LinkProductsList
                        label={'ITEMS LIST'}
                        to={`/department/${item?.department?.toLowerCase()}/${item?.id_list?.id_operation_list?.id}/list/${item?.id_list?.id}`}
                        state={{
                          id_operation: `${item?.id_list?.id_operation_list?.id}`,
                          yachtName: item?.id_yacht?.yacht_name,
                          orderType: item?.service,
                        }}
                        variant='contained'
                        sx={{ backgroundColor: '#009ab6' }}
                        page={`${item?.department}`}
                      />) : ('-')}
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>{formatDate(item?.date)}</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{formatTime(item?.time)}</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{item?.location}</Typography>
                  <Stack alignItems='flex-start'>
                    <Typography sx={{ fontWeight: 600 }}>{driverFullName}</Typography>
                    {item?.driverPhone && item?.driverPhone !== '-' && (
                      <Typography
                        variant='body1'
                        fontWeight={'600'}
                        alignItems={'end'}
                        display={'flex'}
                        justifyItems={'center'}
                      >
                        <LocalPhoneIcon style={{ width: 16, paddingRight: 2 }} />
                        {item?.driverPhone}
                      </Typography>
                    )}
                  </Stack>
                  <DeliveryYachtsStatusLogbookListEdit
                    value={{
                      status_logbook: item?.statusLogbook,
                      id: item?.id,
                      from: 'pickup_suppliers',
                    }}
                    stopPropagation={true}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      <Stack direction='row' justifyContent='flex-end'>
        <Button variant='outlined' onClick={onClose}>
          Close
        </Button>
      </Stack>
    </Stack>
  );
};

export default PickupSuppliersDetailsDrawer;